/**
 * Core implementation for creating property descriptors with default value behavior
 */
function createDefaultPropertyDescriptor<T>(propertyName: string, getDefaultValue: (component: any) => T): PropertyDescriptor {
    const hiddenPropertyKey = Symbol(`__${propertyName}_value`);

    return {
        get(): T {
            const currentValue = (this as any)[hiddenPropertyKey] as T | undefined;

            // Comparing only with `undefined` to avoid comparing with `null`, `0`, `false`, `''`
            return currentValue !== undefined ? currentValue : getDefaultValue(this);
        },
        set(value: T | undefined): void {
            (this as any)[hiddenPropertyKey] = value;
        },
        enumerable: true,
        configurable: true
    };
}

/**
 * ### WORKAROUND UNTIL WE UPGRADE ANGULAR AND HAVE REQUIRED/OPTIONAL INUPTS
 * It aims at allowing the definition of optional inputs with a default value.
 * The idea is to use the `!` assertion so we can avoid defining it as optional with `?`.
 * This way Angular and TypeScript won't force us to assign a value or allow `undefined` values.
 *
 * The input will be optional (a consumer won't have to pass a value) and there will be a default value.
 * If the consumer passes a value, it cannot be `undefined` because the input is not marked as optional.
 *
 * ----------------------------------------------------------------------
 *
 * A TypeScript decorator that provides static default values for class properties.
 *
 * This decorator automatically assigns a default value to a property when it's
 * undefined, ensuring the property always has a meaningful value. It's particularly
 * useful for component inputs that need fallback values.
 *
 * @template T - The type of the property and default value
 * @param {T} defaultValue - The static default value
 * @returns {PropertyDecorator} A decorator function that modifies the property
 *
 * @example
 * ```typescript
 * class MyComponent {
 *     @Input()
 *     @DefaultTo('base-dark') // Static default value
 *     public color!: ColorName;
 *
 *     @Input()
 *     @DefaultTo(false) // Static default value
 *     public disabled!: boolean;
 * }
 * ```
 *
 * @note
 * - Only applies default when the value is explicitly `undefined`
 * - `null`, `0`, `false`, `''` are treated as valid values, not defaults
 * - For property-based defaults, use `@DefaultToProperty` instead
 */
export function DefaultTo<T>(defaultValue: T) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return function (target: any, propertyName: string): void {
        const descriptor = createDefaultPropertyDescriptor(propertyName, () => defaultValue);

        Object.defineProperty(target, propertyName, descriptor);
    };
}

/**
 * A TypeScript decorator that provides property-based default values for class properties.
 *
 * This decorator automatically assigns the value of another property as the default
 * when the decorated property is undefined. It hooks into Angular's OnChanges lifecycle
 * to reactively update the property when dependencies change.
 *
 * @template T - The type of the property
 * @param {string} sourcePropertyName - The name of the property to use as the default value
 * @returns {PropertyDecorator} A decorator function that modifies the property
 *
 * @example
 * ```typescript
 * class MyComponent implements OnChanges {
 *     @Input()
 *     public color!: ColorName;
 *
 *     @Input()
 *     @DefaultToProperty('color') // Will default to the value of 'color' property
 *     public hoverColor!: ColorName;
 *
 *     @Input()
 *     @DefaultToProperty('color') // Will default to the value of 'color' property
 *     public focusColor!: ColorName;
 * }
 * ```
 *
 * @note
 * - Only applies default when the value is explicitly `undefined`
 * - `null`, `0`, `false`, `''` are treated as valid values, not defaults
 * - The source property must exist on the same class instance
 */
export function DefaultToProperty<T>(sourcePropertyName: string) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return function (target: any, propertyName: string): void {
        const descriptor = createDefaultPropertyDescriptor(propertyName, (component) => component[sourcePropertyName] as T);

        Object.defineProperty(target, propertyName, descriptor);
    };
}
